'use client'

import { useCallback, useState } from 'react'
import { useResizeObserver } from '@wojtekmaj/react-hooks'
import { pdfjs, Document, Page } from 'react-pdf'
import { LoadingProcessData } from 'react-pdf/dist/Document'
import Spinner from '@/components/Spinner'
import ProgressCircle from '../ProgressCircle/ProgressCircle'
import IconButton from '../IconButton/IconButton'
import { TransformWrapper, TransformComponent, useControls } from 'react-zoom-pan-pinch'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

const resizeObserverOptions = {}

export default function Digimag({ file }: Readonly<{ file: string }>) {
  const [numPages, setNumPages] = useState<number>()
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [progress, setProgress] = useState<number>(0)
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null)
  const [containerWidth, setContainerWidth] = useState<number>()
  const [pageLoaded, setPageLoaded] = useState<boolean>(false)

  const onResize = useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries
    if (entry) {
      setContainerWidth(entry.contentRect.width)
    }
  }, [])

  useResizeObserver(containerRef, resizeObserverOptions, onResize)

  function onDocumentLoadSuccess(pdf: pdfjs.PDFDocumentProxy) {
    setNumPages(pdf.numPages)
    setPageNumber(1)
  }

  function onLoadProgress({ loaded, total }: LoadingProcessData) {
    setProgress(Math.ceil((loaded / total) * 100))
  }

  function changePage(offset: number) {
    setPageLoaded(false)
    setPageNumber((prevPageNumber) => {
      const newPage = (prevPageNumber || 0) + offset
      if (newPage < 1) return 1
      if (numPages && newPage > numPages) return numPages
      return newPage
    })
  }

  function Controls() {
    const { zoomIn, zoomOut, resetTransform } = useControls()

    return (
      <div className="absolute left-0 top-0 z-10 flex gap-1 p-4">
        <button
          onClick={() => zoomIn()}
          className="rounded-full bg-white bg-opacity-50 px-4 py-2 text-black hover:bg-red-600 hover:bg-opacity-50 hover:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
            <path d="M6.25 8.75v-1h-1a.75.75 0 0 1 0-1.5h1v-1a.75.75 0 0 1 1.5 0v1h1a.75.75 0 0 1 0 1.5h-1v1a.75.75 0 0 1-1.5 0Z" />
            <path
              fillRule="evenodd"
              d="M7 12c1.11 0 2.136-.362 2.965-.974l2.755 2.754a.75.75 0 1 0 1.06-1.06l-2.754-2.755A5 5 0 1 0 7 12Zm0-1.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <button
          onClick={() => zoomOut()}
          className="rounded-full bg-white bg-opacity-50 px-4 py-2 text-black hover:bg-red-600 hover:bg-opacity-50 hover:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
            <path d="M8.75 6.25h-3.5a.75.75 0 0 0 0 1.5h3.5a.75.75 0 0 0 0-1.5Z" />
            <path
              fillRule="evenodd"
              d="M7 12c1.11 0 2.136-.362 2.965-.974l2.755 2.754a.75.75 0 1 0 1.06-1.06l-2.754-2.755A5 5 0 1 0 7 12Zm0-1.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        <button
          onClick={() => resetTransform()}
          className="rounded-full bg-white bg-opacity-50 px-4 py-2 text-black hover:bg-red-600 hover:bg-opacity-50 hover:text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4">
            <path
              fillRule="evenodd"
              d="M2.22 2.22a.75.75 0 0 1 1.06 0L5.5 4.44V2.75a.75.75 0 0 1 1.5 0v3.5a.75.75 0 0 1-.75.75h-3.5a.75.75 0 0 1 0-1.5h1.69L2.22 3.28a.75.75 0 0 1 0-1.06Zm10.5 0a.75.75 0 1 1 1.06 1.06L11.56 5.5h1.69a.75.75 0 0 1 0 1.5h-3.5A.75.75 0 0 1 9 6.25v-3.5a.75.75 0 0 1 1.5 0v1.69l2.22-2.22ZM2.75 9h3.5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-1.69l-2.22 2.22a.75.75 0 0 1-1.06-1.06l2.22-2.22H2.75a.75.75 0 0 1 0-1.5ZM9 9.75A.75.75 0 0 1 9.75 9h3.5a.75.75 0 0 1 0 1.5h-1.69l2.22 2.22a.75.75 0 1 1-1.06 1.06l-2.22-2.22v1.69a.75.75 0 0 1-1.5 0v-3.5Z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <div className={`flex items-center justify-center`}>
      <IconButton
        onClickHandler={() => changePage(-1)}
        disabled={pageNumber === 1 || !pageLoaded}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-10"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
        }
      />
      <div className={`relative mx-6 grow overflow-hidden`} ref={setContainerRef}>
        <Document
          file={file}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadProgress={onLoadProgress}
          loading={<ProgressCircle progress={progress} />}
          error=""
        >
          <div className="relative flex items-center justify-center">
            {!pageLoaded && (
              <div className="absolute left-1/2 top-1/2 z-10">
                <Spinner />
              </div>
            )}
            <TransformWrapper>
              {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                <>
                  {pageNumber !== 1 && pageLoaded && <Controls />}
                  <TransformComponent>
                    <Page
                      loading=""
                      canvasBackground="transparent"
                      onRenderSuccess={() => setPageLoaded(true)}
                      pageNumber={pageNumber}
                      width={pageNumber === 1 || pageNumber === numPages ? (containerWidth ?? 100) / 2 : containerWidth}
                      height={817}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      className={`absolute cursor-grab transition-opacity duration-200 ${pageLoaded ? 'opacity-1' : 'opacity-0'}`}
                    />
                  </TransformComponent>
                </>
              )}
            </TransformWrapper>
          </div>
        </Document>
      </div>
      <IconButton
        onClickHandler={() => changePage(1)}
        disabled={pageNumber === numPages || !pageLoaded}
        icon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="size-10"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        }
      />
    </div>
  )
}
